import type { StateCreator } from "zustand";
import { create } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import { isValidEmail } from "@/components/news-alerts/newsAlertsHelpers";
import type { TimezoneChoices } from "@/data-access/news-alerts/queries/timezone-queries";
import type { BriefingDefinitionDeliverySchedule } from "@/data-access/scheduled-briefings/queries/briefing-definition-queries";
import type { BriefingDefinitionRepositoryError } from "@/lib/scheduled-briefings/repository/briefingDefinitionRepository.types";

export type BriefingCreateStep = "choose-template" | "configure";

/** Uploaded image replacing a template Image slot; only `assetId` is persisted. */
export interface BriefingCreateImageAsset {
  assetId: string;
  /** Upload URL until saved, then the server-hydrated URL from the definition. */
  url: string;
}

export interface BriefingCreateAndEditDefinitionState {
  definitionId: string | null;
  definitionUpdatedAt: string | null;
  templateUpdatedAt: string | null;
  /** From the loaded definition; `false` renders the configure view read-only. */
  canEditDefinition: boolean;
  isFetchingBriefingDefinition: boolean;
  isSavingBriefingDefinition: boolean;
  definitionError: BriefingDefinitionRepositoryError | null;
  /** Bumped by every load, save, and reset; responses for an older id are ignored. Never reset to 0. */
  definitionRequestId: number;
}

export interface BriefingCreateInvalidRecipient {
  id: string;
  value: string;
}

const toInvalidRecipientEntry = (
  value: string,
): BriefingCreateInvalidRecipient => ({
  id: crypto.randomUUID(),
  value,
});

export interface BriefingCreateState
  extends BriefingCreateAndEditDefinitionState {
  step: BriefingCreateStep;
  selectedTemplateId: string | null;
  /** `null` until the user edits the name; the view then shows the template title. */
  briefingName: string | null;
  overallPrompt: string;
  lookbackDays: number;
  slotInputs: Record<string, string>;
  /** Renamed ReportSection headings. Missing keys use the template title. */
  slotTitles: Record<string, string>;
  /** Replaced Image slots. Missing keys use the template Image.src. */
  imageAssets: Record<string, BriefingCreateImageAsset>;
  scheduleFrequency: BriefingDefinitionDeliverySchedule | "";
  scheduleTime: string;
  timezone: TimezoneChoices | "";
  validRecipientEmails: string[];
  invalidRecipientEmails: BriefingCreateInvalidRecipient[];
  recipientInputValue: string;
  /** Form values as last loaded, saved, or seeded; leaving warns when the form differs. */
  savedForm: BriefingCreateForm;
}

type BriefingCreateForm = Pick<
  BriefingCreateState,
  | "briefingName"
  | "overallPrompt"
  | "lookbackDays"
  | "slotInputs"
  | "slotTitles"
  | "imageAssets"
  | "scheduleFrequency"
  | "scheduleTime"
  | "timezone"
  | "validRecipientEmails"
  | "invalidRecipientEmails"
  | "recipientInputValue"
>;

export const pickBriefingCreateForm = (
  state: BriefingCreateForm,
): BriefingCreateForm => ({
  briefingName: state.briefingName,
  overallPrompt: state.overallPrompt,
  lookbackDays: state.lookbackDays,
  slotInputs: state.slotInputs,
  slotTitles: state.slotTitles,
  imageAssets: state.imageAssets,
  scheduleFrequency: state.scheduleFrequency,
  scheduleTime: state.scheduleTime,
  timezone: state.timezone,
  validRecipientEmails: state.validRecipientEmails,
  invalidRecipientEmails: state.invalidRecipientEmails,
  recipientInputValue: state.recipientInputValue,
});

/** Empty slot inputs match unset ones, and recipient ids are ignored. */
const serializeBriefingCreateForm = (form: BriefingCreateForm): string =>
  JSON.stringify({
    ...pickBriefingCreateForm(form),
    slotInputs: Object.entries(form.slotInputs)
      .filter(([, value]) => value !== "")
      .sort(([a], [b]) => a.localeCompare(b)),
    slotTitles: Object.entries(form.slotTitles).sort(([a], [b]) =>
      a.localeCompare(b),
    ),
    imageAssets: Object.entries(form.imageAssets)
      .map(([slotId, asset]) => [slotId, asset.assetId] as const)
      .sort(([left], [right]) => left.localeCompare(right)),
    invalidRecipientEmails: form.invalidRecipientEmails.map(
      (entry) => entry.value,
    ),
  });

export const selectHasUnsavedChanges = (state: BriefingCreateState): boolean =>
  serializeBriefingCreateForm(state) !==
  serializeBriefingCreateForm(state.savedForm);

/** Blocks save: invalid chips would be dropped. A briefing may have no recipients. */
export const selectRecipientsError = (
  state: BriefingCreateState,
): string | undefined =>
  state.invalidRecipientEmails.length > 0
    ? "Fix invalid recipient emails"
    : undefined;

export type BriefingCreateActions = {
  selectTemplate: (templateId: string) => void;
  setBriefingName: (briefingName: string) => void;
  setOverallPrompt: (overallPrompt: string) => void;
  setLookbackDays: (lookbackDays: number) => void;
  setSlotInput: (slotId: string, value: string) => void;
  /** Fills slot inputs the user has not set yet; existing values (including "") are kept. */
  seedSlotInputs: (defaults: Record<string, string>) => void;
  /** Renames a section heading; an empty title falls back to the template title. */
  setSlotTitle: (slotId: string, title: string) => void;
  /** Replaces the template image for `slotId` with an uploaded asset. */
  setImageAsset: (slotId: string, asset: BriefingCreateImageAsset) => void;
  /** Removes the slot replacement so the template image is used. */
  clearImageAsset: (slotId: string) => void;
  setScheduleFrequency: (
    scheduleFrequency: BriefingDefinitionDeliverySchedule | "",
  ) => void;
  setScheduleTime: (scheduleTime: string) => void;
  setTimezone: (timezone: TimezoneChoices | "") => void;
  /** Sets a default timezone without marking the form as changed. */
  seedTimezone: (timezone: TimezoneChoices) => void;
  /** Starts a new briefing with the owner as its recipient without marking the form as changed. */
  seedOwnerRecipient: (email: string) => void;
  setRecipientInputValue: (recipientInputValue: string) => void;
  commitRecipientInput: () => void;
  removeValidRecipient: (email: string) => void;
  removeInvalidRecipient: (id: string) => void;
  setInvalidRecipientValue: (id: string, value: string) => void;
  commitInvalidRecipient: (id: string) => void;
  resetBriefingCreate: () => void;
};

export type BriefingCreateFlowStateSlice = BriefingCreateState &
  BriefingCreateActions;

const briefingCreateAndEditDefinitionInitialState: BriefingCreateAndEditDefinitionState =
  {
    definitionId: null,
    definitionUpdatedAt: null,
    templateUpdatedAt: null,
    canEditDefinition: true,
    isFetchingBriefingDefinition: false,
    isSavingBriefingDefinition: false,
    definitionError: null,
    definitionRequestId: 0,
  };

const briefingCreateFormInitialState: BriefingCreateForm = {
  briefingName: null,
  overallPrompt: "",
  lookbackDays: 1,
  slotInputs: {},
  slotTitles: {},
  imageAssets: {},
  scheduleFrequency: "MONDAY",
  scheduleTime: "7:00 AM",
  timezone: "",
  validRecipientEmails: [],
  invalidRecipientEmails: [],
  recipientInputValue: "",
};

const briefingCreateFlowInitialState: BriefingCreateState = {
  ...briefingCreateAndEditDefinitionInitialState,
  ...briefingCreateFormInitialState,
  step: "choose-template",
  selectedTemplateId: null,
  savedForm: briefingCreateFormInitialState,
};

const createBriefingCreateSlice: StateCreator<
  BriefingCreateFlowStateSlice,
  [["zustand/devtools", never]]
> = (set) => ({
  ...briefingCreateFlowInitialState,
  selectTemplate: (templateId) =>
    set(
      (state) => ({
        selectedTemplateId: templateId,
        step: "configure",
        // Image and section slot ids belong to the previous template.
        ...(state.selectedTemplateId === templateId
          ? {}
          : { imageAssets: {}, slotTitles: {} }),
      }),
      false,
      "[User] Briefing create: Select template",
    ),
  setBriefingName: (briefingName) =>
    set({ briefingName }, false, "[User] Briefing create: Set briefing name"),
  setOverallPrompt: (overallPrompt) =>
    set({ overallPrompt }, false, "[User] Briefing create: Set overall prompt"),
  setLookbackDays: (lookbackDays) =>
    set({ lookbackDays }, false, "[User] Briefing create: Set lookback days"),
  setSlotInput: (slotId, value) =>
    set(
      (state) => ({
        slotInputs: {
          ...state.slotInputs,
          [slotId]: value,
        },
      }),
      false,
      "[User] Briefing create: Set slot input",
    ),
  seedSlotInputs: (defaults) =>
    set(
      (state) => ({
        slotInputs: { ...defaults, ...state.slotInputs },
        savedForm: {
          ...state.savedForm,
          slotInputs: { ...defaults, ...state.savedForm.slotInputs },
        },
      }),
      false,
      "[System] Briefing create: Seed slot inputs",
    ),
  setSlotTitle: (slotId, title) =>
    set(
      (state) => {
        const slotTitles = { ...state.slotTitles };
        const trimmed = title.trim();
        if (trimmed) slotTitles[slotId] = trimmed;
        else delete slotTitles[slotId];
        return { slotTitles };
      },
      false,
      "[User] Briefing create: Set slot title",
    ),
  setImageAsset: (slotId, asset) =>
    set(
      (state) => ({
        imageAssets: {
          ...state.imageAssets,
          [slotId]: asset,
        },
      }),
      false,
      "[User] Briefing create: Set image asset",
    ),
  clearImageAsset: (slotId) =>
    set(
      (state) => {
        const imageAssets = { ...state.imageAssets };
        delete imageAssets[slotId];
        return { imageAssets };
      },
      false,
      "[User] Briefing create: Clear image asset",
    ),
  setScheduleFrequency: (scheduleFrequency) =>
    set(
      { scheduleFrequency },
      false,
      "[User] Briefing create: Set schedule frequency",
    ),
  setScheduleTime: (scheduleTime) =>
    set({ scheduleTime }, false, "[User] Briefing create: Set schedule time"),
  setTimezone: (timezone) =>
    set({ timezone }, false, "[User] Briefing create: Set timezone"),
  seedTimezone: (timezone) =>
    set(
      (state) => ({ timezone, savedForm: { ...state.savedForm, timezone } }),
      false,
      "[System] Briefing create: Seed timezone",
    ),
  seedOwnerRecipient: (email) =>
    set(
      (state) => ({
        validRecipientEmails: [email],
        savedForm: { ...state.savedForm, validRecipientEmails: [email] },
      }),
      false,
      "[System] Briefing create: Seed owner recipient",
    ),
  setRecipientInputValue: (recipientInputValue) =>
    set(
      { recipientInputValue },
      false,
      "[User] Briefing create: Set recipient input",
    ),
  commitRecipientInput: () =>
    set((state) => {
      const email = state.recipientInputValue.trim();
      if (!email) return state;

      const existingValues = [
        ...state.validRecipientEmails,
        ...state.invalidRecipientEmails.map((entry) => entry.value),
      ];
      if (existingValues.includes(email)) {
        return { ...state, recipientInputValue: "" };
      }

      if (isValidEmail(email)) {
        return {
          ...state,
          recipientInputValue: "",
          validRecipientEmails: state.validRecipientEmails.includes(email)
            ? state.validRecipientEmails
            : [...state.validRecipientEmails, email],
        };
      }

      if (state.invalidRecipientEmails.some((entry) => entry.value === email)) {
        return { ...state, recipientInputValue: "" };
      }

      return {
        ...state,
        recipientInputValue: "",
        invalidRecipientEmails: [
          ...state.invalidRecipientEmails,
          toInvalidRecipientEntry(email),
        ],
      };
    }),
  removeValidRecipient: (email) =>
    set(
      (state) => ({
        validRecipientEmails: state.validRecipientEmails.filter(
          (item) => item !== email,
        ),
      }),
      false,
      "[User] Briefing create: Remove valid recipient",
    ),
  removeInvalidRecipient: (id) =>
    set(
      (state) => ({
        invalidRecipientEmails: state.invalidRecipientEmails.filter(
          (entry) => entry.id !== id,
        ),
      }),
      false,
      "[User] Briefing create: Remove invalid recipient",
    ),
  setInvalidRecipientValue: (id, value) =>
    set(
      (state) => ({
        invalidRecipientEmails: state.invalidRecipientEmails.map((entry) =>
          entry.id === id ? { ...entry, value } : entry,
        ),
      }),
      false,
      "[User] Briefing create: Set invalid recipient value",
    ),
  commitInvalidRecipient: (id) =>
    set((state) => {
      const entry = state.invalidRecipientEmails.find((item) => item.id === id);
      const email = entry?.value.trim() ?? "";
      if (!email) {
        return {
          ...state,
          invalidRecipientEmails: state.invalidRecipientEmails.filter(
            (item) => item.id !== id,
          ),
        };
      }

      if (isValidEmail(email)) {
        return {
          ...state,
          invalidRecipientEmails: state.invalidRecipientEmails.filter(
            (item) => item.id !== id,
          ),
          validRecipientEmails: state.validRecipientEmails.includes(email)
            ? state.validRecipientEmails
            : [...state.validRecipientEmails, email],
        };
      }

      return {
        ...state,
        invalidRecipientEmails: state.invalidRecipientEmails.map((item) =>
          item.id === id ? { ...item, value: email } : item,
        ),
      };
    }),
  resetBriefingCreate: () =>
    set(
      (state) => ({
        ...briefingCreateFlowInitialState,
        definitionRequestId: state.definitionRequestId + 1,
      }),
      false,
      "Reset briefing create flow",
    ),
});

const useBriefingCreateStore = create<BriefingCreateFlowStateSlice>()(
  devtools(createBriefingCreateSlice, {
    name: "briefing-create-store",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useBriefingCreateStore;
